import type { CriteriaSet, RecommendationRule } from '../types/ai'

export const criteriaSets: CriteriaSet[] = [
  {
    id: 'cs-mega-assessment',
    track: 'mega',
    stage: 'assessment',
    label: 'Mega Event · Assessment',
    bands: { autoAdvance: 80, manualReview: 60 },
    criteria: [
      { id: 'strategic-fit', label: 'Strategic fit with national agenda', description: 'Alignment to UAE Centennial 2071, We the UAE 2031 and sector strategies.', weight: 25 },
      { id: 'economic-impact', label: 'Economic impact & GDP contribution', description: 'Modelled direct, indirect and induced impact against support requested.', weight: 20 },
      { id: 'entity-capability', label: 'Entity delivery capability', description: 'Track record, governance maturity and resourcing of the applying entity.', weight: 15 },
      { id: 'infrastructure', label: 'Infrastructure & venue readiness', description: 'Existing venue, transport, accommodation and utilities capacity.', weight: 15 },
      { id: 'financial', label: 'Financial viability & co-funding', description: 'Committed co-funding, sponsorship pipeline, contingency provisions.', weight: 15 },
      { id: 'legacy', label: 'Legacy & sustainability', description: 'Post-event asset use, community benefit and environmental commitments.', weight: 10 }
    ]
  },
  {
    id: 'cs-major-assessment',
    track: 'major',
    stage: 'assessment',
    label: 'Major Event · Assessment',
    bands: { autoAdvance: 76, manualReview: 55 },
    criteria: [
      { id: 'strategic-fit', label: 'Sector strategy alignment', description: 'Alignment to the sponsoring sector strategy and emirate priorities.', weight: 25 },
      { id: 'economic-impact', label: 'Economic & reputational impact', description: 'Visitor nights, media value and sector positioning.', weight: 20 },
      { id: 'entity-capability', label: 'Entity delivery capability', description: 'Operating history and governance of the applying entity.', weight: 20 },
      { id: 'infrastructure', label: 'Venue & logistics fit', description: 'Suitability of the proposed venue and logistics plan.', weight: 15 },
      { id: 'financial', label: 'Financial viability', description: 'Budget realism and co-funding secured.', weight: 20 }
    ]
  },
  {
    id: 'cs-international-assessment',
    track: 'international',
    stage: 'assessment',
    label: 'International Event · Standards Assessment',
    bands: { autoAdvance: 72, manualReview: 52 },
    criteria: [
      { id: 'standards', label: 'Event standards compliance', description: 'Meets SDU minimum standards for safety, licensing and delivery quality.', weight: 30 },
      { id: 'audience', label: 'International audience draw', description: 'Share of overseas attendance and inbound tourism value.', weight: 25 },
      { id: 'brand', label: 'Brand alignment with UAE narrative', description: 'Fit with national brand positioning and content suitability.', weight: 25 },
      { id: 'readiness', label: 'Organiser marketing readiness', description: 'Assets, channels and lead time available for amplification.', weight: 20 }
    ]
  },
  {
    id: 'cs-mega-bid',
    track: 'mega',
    stage: 'bid',
    label: 'Mega Event · Bid Readiness',
    bands: { autoAdvance: 85, manualReview: 65 },
    criteria: [
      { id: 'host-requirements', label: 'Host-body requirement coverage', description: 'Percentage of mandatory host-body requirements evidenced.', weight: 35 },
      { id: 'guarantees', label: 'Government guarantees issued', description: 'Security, customs, visa, tax and financial guarantee letters.', weight: 25 },
      { id: 'dossier', label: 'Bid dossier completeness', description: 'Technical volumes, venue master plan and operating budget.', weight: 25 },
      { id: 'competitive', label: 'Competitive position', description: 'Relative strength versus declared competing host cities.', weight: 15 }
    ]
  },
  {
    id: 'cs-mega-readiness',
    track: 'mega',
    stage: 'readiness',
    label: 'Mega Event · Operational Readiness',
    bands: { autoAdvance: 90, manualReview: 75 },
    criteria: [
      { id: 'venue', label: 'Venue & overlay readiness', description: 'Completion of overlay, testing and handover.', weight: 20 },
      { id: 'transport', label: 'Transport & mobility', description: 'Capacity modelling, fleet readiness, last-mile plans.', weight: 20 },
      { id: 'security', label: 'Safety & security', description: 'Integrated command, accreditation and emergency plans.', weight: 25 },
      { id: 'workforce', label: 'Workforce & volunteers', description: 'Recruitment, training and deployment completion.', weight: 20 },
      { id: 'technology', label: 'Technology & broadcast', description: 'Networks, ticketing, timing and broadcast rehearsal.', weight: 15 }
    ]
  }
]

export const recommendationRules: RecommendationRule[] = [
  {
    id: 'rr-1',
    stage: 'assessment',
    condition: 'Weighted score in manual-review band and co-funding below 30%',
    recommendation: 'Request a revised funding structure before advancing to Bid',
    action: 'Issue "Request Information" with co-funding template',
    triggers: 34,
    acceptanceRate: 88,
    active: true
  },
  {
    id: 'rr-2',
    stage: 'assessment',
    condition: 'Entity has no delivered event above AED 50M in last 5 years',
    recommendation: 'Attach an SDU delivery partner to the entity for the lifecycle',
    action: 'Assign delivery assurance officer at Assessment gate',
    triggers: 19,
    acceptanceRate: 74,
    active: true
  },
  {
    id: 'rr-3',
    stage: 'bid',
    condition: 'Host-body mandatory requirement marked gap within 90 days of deadline',
    recommendation: 'Escalate the requirement gap to the federal alignment committee',
    action: 'Create escalation item and notify sponsoring ministry',
    triggers: 27,
    acceptanceRate: 91,
    active: true
  },
  {
    id: 'rr-4',
    stage: 'bid',
    condition: 'Competing host city strength exceeds UAE position on two or more axes',
    recommendation: 'Commission a differentiation study on legacy and sustainability',
    action: 'Generate bid-readiness report with competitor annex',
    triggers: 12,
    acceptanceRate: 66,
    active: true
  },
  {
    id: 'rr-5',
    stage: 'approval',
    condition: 'Any mandatory stakeholder sign-off outstanding at committee date',
    recommendation: 'Approve conditionally with sign-off deadline attached',
    action: 'Record conditional approval with condition tracker',
    triggers: 22,
    acceptanceRate: 82,
    active: true
  },
  {
    id: 'rr-6',
    stage: 'plan-build',
    condition: 'Budget drawn exceeds 60% while schedule completion below 40%',
    recommendation: 'Trigger a cost-to-complete review before next gate',
    action: 'Flag record and generate stage-gate review report',
    triggers: 15,
    acceptanceRate: 79,
    active: true
  },
  {
    id: 'rr-7',
    stage: 'readiness',
    condition: 'Any readiness domain scoring below its threshold',
    recommendation: 'Block advancement and schedule a targeted rehearsal',
    action: 'Hold gate and notify domain owner',
    triggers: 9,
    acceptanceRate: 95,
    active: true
  },
  {
    id: 'rr-8',
    stage: 'deliver-operate',
    condition: 'Service level below 90% for two consecutive operating days',
    recommendation: 'Deploy the SDU surge operations team',
    action: 'Raise operational incident and notify director',
    triggers: 6,
    acceptanceRate: 71,
    active: true
  },
  {
    id: 'rr-9',
    stage: 'outreach',
    condition: 'Channel delivering below 70% of guaranteed impressions at mid-campaign',
    recommendation: 'Reallocate inventory to higher-performing ecosystem partners',
    action: 'Rebalance placements and notify outreach manager',
    triggers: 31,
    acceptanceRate: 84,
    active: true
  },
  {
    id: 'rr-10',
    stage: 'outreach',
    condition: 'Overseas audience share above 55% on a Silver package',
    recommendation: 'Offer a Gold tier upgrade with embassy network activation',
    action: 'Create upgrade proposal for the entity',
    triggers: 8,
    acceptanceRate: 62,
    active: false
  }
]
